/**
 * Website Order List — standalone page (Bulk > Bulk Order > Website Order),
 * sibling to Bulk Order List, not a tab inside it.
 *
 * Lists self-serve orders placed via the buyume-web storefront:
 *   BulkOrder.find({ source: "website", websiteOrderReference: {$exists:true} })
 * See project_website_order_tab_and_chat memory for the original design.
 *
 * Expanding a row shows the buyer's WhatsApp thread (WebsiteOrderChatPanel)
 * inline; the "Open in My Chats ↗" button next to it instead opens the same
 * conversation in the full My Chats screen, in a new tab
 * (/crm/dashboard/my-chats/?chat=<conversationId>; was ai-chats until
 * 2026-10-14, which exposed the whole AI inbox) — added 2026-08-18 per
 * Shailendra, since the inline panel is deliberately a trimmed-down reader
 * (no assign/NI/attach — see WebsiteOrderChatPanel.js's own header comment)
 * and sometimes ops need the real thing.
 *
 * Also added 2026-08-18: a second table listing every buyume-web
 * "Request Callback" (max slab tier) request, and — on BOTH tables — call
 * history (last call date/time/remark/agent, total calls, from CallReports)
 * and an "Order Placed" indicator (any BulkOrder at status 1/8 for that
 * phone, expandable to the order list). See callbackRequestOps.js
 * (manijment-api) for the backend side.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import {
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Button,
  Box,
  Collapse,
  Chip,
} from "@material-ui/core";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import IconButton from "@material-ui/core/IconButton";
import { LoadingButton } from "@material-ui/lab";
import MainCard from "../../ui-component/cards/MainCard";
import WebsiteOrderChatPanel from "./WebsiteOrderChatPanel";
import { SoftphoneButton } from "../../ui-component/SoftphoneCall";

const AI_PATH = process.env.REACT_APP_AI_PATH;

const to10 = (p) => String(p || "").replace(/\D/g, "").slice(-10);

const ORDER_STATUS_LABEL = { 1: "Placed", 8: "Splitted" };

// "Open in My Chats" — new tab, full My Chats screen (not the trimmed inline
// panel). No conversationId means this buyer never messaged us on WhatsApp.
function OpenInMyChatsButton({ conversationId }) {
  if (!conversationId) return <Typography variant="caption" color="textSecondary">No WA chat</Typography>;
  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<OpenInNewIcon fontSize="small" />}
      onClick={() => window.open(`/crm/dashboard/my-chats/?chat=${conversationId}`, "_blank", "noopener,noreferrer")}
    >
      My Chats
    </Button>
  );
}

// Call-history cell used by both tables — last call date/time, remark, agent.
function LastCallCell({ call }) {
  if (!call || !call.lastCallAt) {
    return <Typography variant="caption" color="textSecondary">No calls logged</Typography>;
  }
  return (
    <Box>
      <Typography variant="caption" display="block">{moment(call.lastCallAt).format("HH:mm A - DD/MM/YYYY")}</Typography>
      {call.lastCallRemark && <Typography variant="caption" display="block" color="textSecondary">{call.lastCallRemark}</Typography>}
      <Typography variant="caption" display="block" color="textSecondary">
        by {call.lastCallAgentName || "—"} · {call.totalCalls} call{call.totalCalls === 1 ? "" : "s"} total
      </Typography>
    </Box>
  );
}

// "Order Placed" cell — badge + expand-to-list-orders, shared by both tables.
function OrderPlacedCell({ orders, expanded, onToggle }) {
  if (!orders || orders.length === 0) {
    return <Chip size="small" label="No" variant="outlined" />;
  }
  return (
    <Box>
      <Chip
        size="small"
        color="primary"
        label={`Yes (${orders.length})`}
        onClick={onToggle}
        onDelete={onToggle}
        deleteIcon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box mt={1}>
          {orders.map((o) => (
            <Box key={o.id} mb={0.5}>
              <Link to={`/bulkorderdetails/${o.id}`} target="_blank" rel="noopener noreferrer">
                <Typography variant="caption">
                  {o.websiteOrderReference || o.numericOrderId || o.id.slice(-6).toUpperCase()} — {ORDER_STATUS_LABEL[o.status] || o.status} — ₹{o.totalPayableAmount} — {moment(o.createdAt).format("DD/MM/YYYY")}
                </Typography>
              </Link>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function WebsiteOrderList() {
  const [date, setDate] = useState({
    startDate: moment().clone().startOf("month").format("YYYY-MM-DD"),
    endDate: new Date().toISOString().slice(0, 10),
  });
  const [parentBulkNumericOrderId, setParentBulkNumericOrderId] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [expandedOrderPlacedId, setExpandedOrderPlacedId] = useState(null);

  // phone(10-digit) -> { call, ordersPlaced, waConversationId } — batch-
  // loaded once per page of orders via POST /web/phones-info.
  const [phoneInfo, setPhoneInfo] = useState({});

  // Call Requests table (separate data source: webcallbackrequests, already
  // enriched server-side — see callbackRequestOps.listCallbackRequests).
  const [callRequests, setCallRequests] = useState([]);
  const [callRequestsLoading, setCallRequestsLoading] = useState(false);
  const [expandedCallReqOrderPlacedId, setExpandedCallReqOrderPlacedId] = useState(null);

  const loadPhoneInfo = (rows) => {
    const phones = [...new Set(rows.map((o) => to10(o.phoneNumber)).filter((p) => p.length === 10))];
    if (!phones.length) return;
    axios
      .post(`${AI_PATH}/api/ai/web/phones-info`, { phones })
      .then((res) => {
        if (res.data?.success) setPhoneInfo((prev) => ({ ...prev, ...res.data.info }));
      })
      .catch((error) => console.log("error loading phone info", error));
  };

  const loadOrders = (p = page, row = rowsPerPage) => {
    setLoading(true);
    axios
      .post(process.env.REACT_APP_PATH_BULK + "/sales/getBulkOrders", {
        source: "website",
        page: p + 1,
        pageLimit: row,
        startDate: date.startDate,
        endDate: date.endDate,
        parentBulkNumericOrderId: parentBulkNumericOrderId ? +parentBulkNumericOrderId : undefined,
      })
      .then((res) => {
        if (res.data.success) {
          const rows = res.data.data[0].data || [];
          setOrders(rows);
          setTotalDocuments(res.data.data[0]?.metadata[0]?.totalDocuments || 0);
          loadPhoneInfo(rows);
        } else {
          alert(res.data?.message || "Someting went wrong");
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log("error of get website orders", error);
        setLoading(false);
      });
  };

  const loadCallRequests = () => {
    setCallRequestsLoading(true);
    axios
      .get(`${AI_PATH}/api/ai/web/callback-requests`)
      .then((res) => {
        if (res.data?.success) setCallRequests(res.data.requests || []);
        setCallRequestsLoading(false);
      })
      .catch((error) => {
        console.log("error loading call requests", error);
        setCallRequestsLoading(false);
      });
  };

  React.useEffect(() => {
    loadOrders(0, rowsPerPage);
    loadCallRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    loadOrders(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRow = parseInt(event.target.value, 10);
    setRowsPerPage(newRow);
    setPage(0);
    loadOrders(0, newRow);
  };

  return (
    <>
      <MainCard content={false} title="Website Order">
        <Grid container spacing={2} alignItems={"center"} style={{ padding: 16 }}>
          <Grid xs={12} sm={12} md={6} lg={3} item>
            <TextField
              id="datetime-local"
              label="Start Date"
              type="date"
              value={date.startDate}
              InputLabelProps={{ shrink: true }}
              style={{ width: "100%" }}
              onChange={(e) => setDate({ ...date, startDate: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <TextField
              id="datetime-local"
              label="End Date"
              type="date"
              value={date.endDate}
              InputLabelProps={{ shrink: true }}
              style={{ width: "100%" }}
              onChange={(e) => setDate({ ...date, endDate: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <TextField
              label="Website Order Reference"
              value={parentBulkNumericOrderId}
              fullWidth
              onChange={(e) => setParentBulkNumericOrderId(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <LoadingButton
              variant="contained"
              style={{ width: "100%" }}
              loadingIndicator="loading..."
              onClick={() => {
                setPage(0);
                loadOrders(0, rowsPerPage);
              }}
              loading={loading}
            >
              Submit
            </LoadingButton>
          </Grid>
        </Grid>

        <Box style={{ padding: "0 16px 8px" }}>
          <Button variant="contained" onClick={() => loadOrders(page, rowsPerPage)}>
            Refresh
          </Button>
        </Box>

        <TableContainer style={{ overflowX: "auto", width: "100%" }}>
          <Table aria-label="website orders table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", padding: "16px" }}></TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 100 }}>Order Ref</TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>Phone Number</TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 180 }}>Order Date</TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 200 }}>Business Name</TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 120 }}>Total Payable</TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 170 }}>Last Call</TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 130 }}>Order Placed</TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>Details</TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 130 }}>My Chats</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={11} align="center">Loading...</TableCell>
                </TableRow>
              )}
              {!loading && orders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center">No website orders found</TableCell>
                </TableRow>
              )}
              {orders.map((order) => {
                const isExpanded = expandedOrderId === order._id;
                const p10 = to10(order.phoneNumber);
                const info = phoneInfo[p10];
                const orderPlacedExpanded = expandedOrderPlacedId === order._id;
                return (
                  <React.Fragment key={order._id}>
                    <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                      <TableCell>
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => setExpandedOrderId(isExpanded ? null : order._id)}
                        >
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{order.websiteOrderReference || "—"}</TableCell>
                      <TableCell>{order?.name}</TableCell>
                      <TableCell>{order?.phoneNumber}</TableCell>
                      <TableCell>{moment(order.createdAt).format("HH:mm A - DD/MM/YYYY")}</TableCell>
                      <TableCell>{order?.seller?.businessName || "not found"}</TableCell>
                      <TableCell>{order?.totalPayableAmount}</TableCell>
                      <TableCell><LastCallCell call={info?.call} /></TableCell>
                      <TableCell>
                        <OrderPlacedCell
                          orders={info?.ordersPlaced}
                          expanded={orderPlacedExpanded}
                          onToggle={() => setExpandedOrderPlacedId(orderPlacedExpanded ? null : order._id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Link to={`/bulkorderdetails/${order._id}`} target="_blank" rel="noopener noreferrer">
                          View Details
                        </Link>
                      </TableCell>
                      <TableCell>
                        <OpenInMyChatsButton conversationId={info?.waConversationId} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Customer Chat
                            </Typography>
                            {isExpanded && order?.phoneNumber && (
                              <WebsiteOrderChatPanel phoneNumber={order.phoneNumber} />
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 100, 200]}
          component="div"
          count={totalDocuments}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </MainCard>

      <Box mt={3}>
        <MainCard content={false} title="Call Requests">
          <Box style={{ padding: "0 16px 8px", paddingTop: 16 }}>
            <Button variant="contained" onClick={loadCallRequests}>
              Refresh
            </Button>
          </Box>
          <TableContainer style={{ overflowX: "auto", width: "100%" }}>
            <Table aria-label="call requests table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>Requested</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>Phone Number</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 220 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 120 }}>Tier</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 100 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 170 }}>Last Call</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 130 }}>Order Placed</TableCell>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 130 }}>My Chats</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {callRequestsLoading && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">Loading...</TableCell>
                  </TableRow>
                )}
                {!callRequestsLoading && callRequests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">No call requests found</TableCell>
                  </TableRow>
                )}
                {callRequests.map((r) => {
                  const expanded = expandedCallReqOrderPlacedId === r.id;
                  return (
                    <TableRow key={r.id}>
                      <TableCell>{moment(r.createdAt).format("HH:mm A - DD/MM/YYYY")}</TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>
                        {/* Click-to-call via the softphone; refresh after the
                            post-call form so Last Call updates. */}
                        <Box display="flex" alignItems="center">
                          {r.phoneNumber}
                          <SoftphoneButton
                            number={to10(r.phoneNumber)}
                            title={`Call ${r.name || r.phoneNumber}`}
                            color="#1565c0"
                            onDispositionClosed={loadCallRequests}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>{r.productName}</TableCell>
                      <TableCell>{r.tierLabel} @ ₹{r.ratePerPiece}/pc</TableCell>
                      <TableCell><Chip size="small" label={r.status} /></TableCell>
                      <TableCell>
                        <LastCallCell call={r.lastCallAt ? {
                          lastCallAt: r.lastCallAt,
                          lastCallRemark: r.lastCallRemark,
                          lastCallAgentName: r.lastCallAgentName,
                          totalCalls: r.totalCalls,
                        } : null} />
                      </TableCell>
                      <TableCell>
                        <OrderPlacedCell
                          orders={r.ordersPlaced}
                          expanded={expanded}
                          onToggle={() => setExpandedCallReqOrderPlacedId(expanded ? null : r.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <OpenInMyChatsButton conversationId={r.waConversationId} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </MainCard>
      </Box>
    </>
  );
}
